import React, { Component } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import moment from 'moment'
import {
  Spin,
  Table,
  Column,
  Checkbox,
  Dropdown,
  Menu,
  Icon,
} from './common/Ant'
import { Anchor } from './common/Anchor'
import { Bread, Crumb } from './common/Bread'
import Divider from './common/Divider'
import Label from './common/Label'
import SearchFilter from './common/SearchFilter'
import Select, { Option } from './common/Select'
import { createQueryString } from '../utils/queryParams'
import { escapeRegExp } from '../utils/helpers'
import { ALL, SITE_STATUS, VIEW_ALL } from '../constants'
import { toTitleCase, renderSiteStatus } from '../utils/textFormatters'
import api from '../services/api'
import {
  downloadBillingReportCsv,
  downloadBillingReportPdf,
} from '../services/billingReportExport'

const EMPTY_QUERY = {}

class BillingReport extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }

  state = {
    allSites: [],
    selectedSiteSlugs: [],
    status: [SITE_STATUS.ACTIVE, SITE_STATUS.INSTALLING, SITE_STATUS.UNINSTALL],
    isLoadingSites: true,
    isLoadingSiteAssets: false,
    siteAssetCountsBySlug: {},
    siteSearchText: '',
    sortedInfo: {},
  }

  async componentDidMount() {
    await this.loadSitesForStatusFilter(this.state.status)
  }

  getStatusesForFilter = statusFilter => {
    const selectedValues = statusFilter || []
    const selectedStatuses = selectedValues.filter(x => x !== ALL)
    if (selectedStatuses.length === 0) {
      return Object.values(SITE_STATUS)
    }
    return selectedStatuses
  }

  loadSitesForStatusFilter = async statusFilter => {
    this.setState({ isLoadingSites: true })
    try {
      const statuses = this.getStatusesForFilter(statusFilter)
      const responses = await api.getAllSites(
            createQueryString({
              perPage: ALL,
              full: 't',
              status: statuses,
            })
          )
      const allItems = responses.items || []
      const uniqueBySlug = allItems.reduce((acc, site) => {
        acc[site.slug] = site
        return acc
      }, {})
      const allSites = Object.values(uniqueBySlug).sort(sortBy('name'))
      const selectedSiteSlugs = allSites.map(s => s.slug)

      this.setState({
        allSites,
        selectedSiteSlugs,
      })
      await this.loadSiteAssetCounts(selectedSiteSlugs)
    } finally {
      this.setState({ isLoadingSites: false })
    }
  }

  countByStatus = (items = [], statusKey = 'status') =>
    items.filter(
      x =>
        x &&
        ['DEPLOYED', 'SPARE'].indexOf(
          `${x[statusKey] || x.status || ''}`.toUpperCase()
        ) > -1
    ).length

  countGroupedByStatus = group => {
    if (!group) {
      return 0
    }
    if (Array.isArray(group)) {
      return this.countByStatus(group)
    }
    return Object.keys(group).reduce((sum, key) => {
      const isTarget = ['DEPLOYED', 'SPARE'].indexOf(key.toUpperCase()) > -1
      const list = Array.isArray(group[key]) ? group[key] : []
      return sum + (isTarget ? list.length : 0)
    }, 0)
  }

  getSiteAssetCounts = payload => ({
    gateway: this.countByStatus(payload.gateways || [], 'gatewayStatus'),
    smart: this.countGroupedByStatus(payload.pods),
    leak: this.countGroupedByStatus(payload.leakPods),
    evac: this.countGroupedByStatus(payload.evacPods),
    meter: this.countByStatus(payload.flowMonitors || []),
    valve: this.countByStatus(payload.waterValves || payload.valves || []),
  })

  loadSiteAssetCounts = async siteSlugs => {
    this.setState({ isLoadingSiteAssets: true })
    try {
      if (!siteSlugs.length) {
        this.setState({ siteAssetCountsBySlug: {} })
        return
      }
      const responses = await Promise.allSettled(
        siteSlugs.map(slug => api.getSiteAssets(slug))
      )
      const siteAssetCountsBySlug = responses.reduce((acc, result, index) => {
        const payload = result.status === 'fulfilled' ? result.value : {}
        acc[siteSlugs[index]] = this.getSiteAssetCounts(payload || {})
        return acc
      }, {})
      this.setState({ siteAssetCountsBySlug })
    } finally {
      this.setState({ isLoadingSiteAssets: false })
    }
  }

  handleStatusChange = value => {
    const selectedValues = value || []
    const hasViewAll = selectedValues.indexOf(ALL) > -1
    const selectedStatuses = selectedValues.filter(x => x !== ALL)
    const isViewAll = selectedStatuses.length === 0
    const newValue =
      hasViewAll && selectedStatuses.length > 0 ? selectedStatuses : isViewAll ? [ALL] : selectedStatuses

    this.setState({
      status: newValue,
    })
    this.loadSitesForStatusFilter(newValue)
  }

  setSiteSearchText = siteSearchText => this.setState({ siteSearchText })

  noopFetch = () => {}

  filterSitesBySearch = (sites, searchText) => {
    const q = (searchText || '').trim()
    if (!q) {
      return sites
    }
    const regex = new RegExp(escapeRegExp(q), 'i')
    return sites.filter(
      s =>
        (s.name && regex.test(s.name)) || (s.slug && regex.test(s.slug))
    )
  }

  sortColumnKeyMatches = (sortedInfo, columnKey) =>
    (sortedInfo && sortedInfo.columnKey === columnKey) ||
    (sortedInfo && sortedInfo.field === columnKey)

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({ sortedInfo: sorter })
  }

  getSortableAssetCount = (record, assetParams, fallback) => {
    const raw = this.getAssetCount({ record, ...assetParams })
    if (raw !== undefined && raw !== null) {
      return Number(raw)
    }
    if (fallback !== undefined && fallback !== null) {
      return Number(fallback)
    }
    return -1
  }

  compareAssetCounts = (a, b, assetParams, fallbackAccessor) => {
    const fa = fallbackAccessor ? fallbackAccessor(a) : undefined
    const fb = fallbackAccessor ? fallbackAccessor(b) : undefined
    return (
      this.getSortableAssetCount(a, assetParams, fa) -
      this.getSortableAssetCount(b, assetParams, fb)
    )
  }

  toggleSiteSelection = (siteSlug, checked) => {
    this.setState(prevState => {
      const { selectedSiteSlugs } = prevState
      if (checked) {
        if (selectedSiteSlugs.indexOf(siteSlug) > -1) {
          return null
        }
        return { selectedSiteSlugs: [...selectedSiteSlugs, siteSlug] }
      }
      return {
        selectedSiteSlugs: selectedSiteSlugs.filter(x => x !== siteSlug),
      }
    })
  }

  renderCount = value =>
    value === 0 || value ? value : '--'

  getDeployedAndSpareCount = (items = [], statusKey = 'status') =>
    items.filter(
      x => x && ['DEPLOYED', 'SPARE'].indexOf(x[statusKey] || x.status) > -1
    ).length

  getAssetCount = ({
    record,
    assetType,
    statsKey,
    arrayKey,
    altArrayKey,
    statusKey,
  }) => {
    const siteAssetCounts =
      this.state.siteAssetCountsBySlug &&
      this.state.siteAssetCountsBySlug[record.slug]
    if (
      siteAssetCounts &&
      assetType &&
      typeof siteAssetCounts[assetType] === 'number'
    ) {
      return siteAssetCounts[assetType]
    }

    const stats = record.stats && record.stats[statsKey]
    const fromStats =
      (stats && (stats.deployedCount || 0)) + (stats && (stats.spareCount || 0))
    if (fromStats > 0) {
      return fromStats
    }

    const arr = record[arrayKey] || record[altArrayKey] || []
    const fromArray = this.getDeployedAndSpareCount(arr, statusKey)
    if (fromArray > 0) {
      return fromArray
    }

    return (stats && stats.totalCount) || arr.length || undefined
  }

  exportCount = record => (params, fallback) => {
    const raw = this.getAssetCount({ record, ...params })
    let v
    if (raw !== undefined && raw !== null) {
      v = raw
    } else if (fallback !== undefined && fallback !== null) {
      v = fallback
    } else {
      v = undefined
    }
    return v === 0 || v ? v : ''
  }

  /** Only sites that are checked and visible with the current status filter. */
  buildExportRows = (filteredSites, selectedSiteSlugs) => {
    const selected = new Set(selectedSiteSlugs)
    const sites = filteredSites.filter(s => selected.has(s.slug))
    return sites.map(record => ({
      name: record.name || '',
      status:
        renderSiteStatus(record.status) || record.status || '',
      gateway: this.exportCount(record)(
        {
          assetType: 'gateway',
          statsKey: 'gateway',
          arrayKey: 'gateways',
          altArrayKey: 'gateway',
          statusKey: 'gatewayStatus',
        },
        record.gatewayCount
      ),
      smart: this.exportCount(record)({
        assetType: 'smart',
        statsKey: 'smartPod',
        arrayKey: 'pods',
        altArrayKey: 'smartPods',
      }),
      leak: this.exportCount(record)({
        assetType: 'leak',
        statsKey: 'leakPod',
        arrayKey: 'leakPods',
      }),
      evac: this.exportCount(record)({
        assetType: 'evac',
        statsKey: 'evacPod',
        arrayKey: 'evacPods',
      }),
      meter: this.exportCount(record)({
        assetType: 'meter',
        statsKey: 'flowMonitor',
        arrayKey: 'flowMonitors',
      }),
      valve: this.exportCount(record)({
        assetType: 'valve',
        statsKey: 'waterValve',
        arrayKey: 'waterValves',
        altArrayKey: 'valves',
      }),
    }))
  }

  handleExportMenuClick = ({ key }) => {
    const { selectedSiteSlugs, allSites, siteSearchText } = this.state
    const visibleSites = this.filterSitesBySearch(allSites, siteSearchText)
    const rows = this.buildExportRows(visibleSites, selectedSiteSlugs)
    const subtitle = `Report date: ${moment().format('MMM D, YYYY')}`
    if (key === 'csv') {
      downloadBillingReportCsv(rows)
    } else if (key === 'pdf') {
      downloadBillingReportPdf(rows, undefined, subtitle)
    }
  }

  render() {
    const { baseUrl, description } = this.props
    const {
      selectedSiteSlugs,
      status,
      allSites,
      isLoadingSites,
      siteSearchText,
      sortedInfo,
    } = this.state
    const sortedSites = allSites
    const visibleSites = this.filterSitesBySearch(sortedSites, siteSearchText)
    const emptyText =
      siteSearchText.trim() && visibleSites.length === 0
        ? 'No sites match your search.'
        : 'No sites match this filter.'
    return (
      <section className="mb5 BillingReport">
        <div className="mb3">
          <Bread>
            <Crumb>
              <Anchor to={baseUrl}>Internal Reports</Anchor>
            </Crumb>
            <Crumb>Billing Report</Crumb>
          </Bread>
        </div>
        <Divider />
        <p className="black-70 lh-copy w-100 tc">{description}</p>
        <Divider />
        <div className="flex-l">
          <div className="w-100 w-50-l pr3-l mb3 mb0-l">
            <Select
              label="Status"
              placeholder="Status"
              mode="multiple"
              input={{
                value: status,
                onChange: this.handleStatusChange,
              }}
              filterable
            >
              {[undefined, ...Object.keys(SITE_STATUS)].map(x => (
                <Option value={SITE_STATUS[x] || 'all'} key={x || 'all'}>
                  {toTitleCase(x) || VIEW_ALL}
                </Option>
              ))}
            </Select>
          </div>
          <div className="w-100 w-50-l mb3">
            <Label>Report date</Label>
            <div className="db black-80 lh-copy pt1">
              {moment().format('MMM D, YYYY')}
            </div>
          </div>
        </div>
        <div className="mb3">
          <SearchFilter
            searchText={siteSearchText}
            setSearchText={this.setSiteSearchText}
            getItems={this.noopFetch}
            query={EMPTY_QUERY}
            placeholder="Search sites"
          />
        </div>
        {isLoadingSites ? (
          <Spin size="large" className="w-100 center mv4" />
        ) : (
          <div>
            <div className="mt3 flex justify-end mb2">
              <Dropdown
                overlay={
                  <Menu onClick={this.handleExportMenuClick}>
                    <Menu.Item key="csv">CSV (.csv)</Menu.Item>
                    <Menu.Item key="pdf">PDF (.pdf)</Menu.Item>
                  </Menu>
                }
                trigger={['click']}
              >
                <a className="ant-dropdown-link pointer">
                  <Icon type="download" className="mr1" />
                  Download
                  <Icon type="down" />
                </a>
              </Dropdown>
            </div>
            <div>
              <Table
                bordered
                dataSource={visibleSites}
                rowKey="slug"
                pagination={false}
                tableLayout="auto"
                locale={{ emptyText }}
                onChange={this.handleTableChange}
              >
                <Column
                  title=""
                  width={60}
                  render={(text, record) => (
                    <Checkbox
                      checked={selectedSiteSlugs.indexOf(record.slug) > -1}
                      onChange={ev =>
                        this.toggleSiteSelection(record.slug, ev.target.checked)
                      }
                    />
                  )}
                />
                <Column
                  key="name"
                  title="Name"
                  dataIndex="name"
                  sorter={sortBy('name')}
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'name') &&
                    sortedInfo.order
                  }
                />
                <Column
                  key="status"
                  title="Status"
                  dataIndex="status"
                  sorter={(a, b) =>
                    String(
                      renderSiteStatus(a.status) || a.status || ''
                    ).localeCompare(
                      String(
                        renderSiteStatus(b.status) || b.status || ''
                      ),
                      undefined,
                      { sensitivity: 'base' }
                    )
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'status') &&
                    sortedInfo.order
                  }
                  render={text => renderSiteStatus(text)}
                />
                <Column
                  key="gateway"
                  title="Gateway"
                  sorter={(a, b) =>
                    this.compareAssetCounts(
                      a,
                      b,
                      {
                        assetType: 'gateway',
                        statsKey: 'gateway',
                        arrayKey: 'gateways',
                        altArrayKey: 'gateway',
                        statusKey: 'gatewayStatus',
                      },
                      r => r.gatewayCount
                    )
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'gateway') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'gateway',
                        statsKey: 'gateway',
                        arrayKey: 'gateways',
                        altArrayKey: 'gateway',
                        statusKey: 'gatewayStatus',
                      }) || record.gatewayCount
                    )
                  }
                />
                <Column
                  key="smart"
                  title="Smart"
                  sorter={(a, b) =>
                    this.compareAssetCounts(a, b, {
                      assetType: 'smart',
                      statsKey: 'smartPod',
                      arrayKey: 'pods',
                      altArrayKey: 'smartPods',
                    })
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'smart') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'smart',
                        statsKey: 'smartPod',
                        arrayKey: 'pods',
                        altArrayKey: 'smartPods',
                      })
                    )
                  }
                />
                <Column
                  key="leak"
                  title="Leak"
                  sorter={(a, b) =>
                    this.compareAssetCounts(a, b, {
                      assetType: 'leak',
                      statsKey: 'leakPod',
                      arrayKey: 'leakPods',
                    })
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'leak') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'leak',
                        statsKey: 'leakPod',
                        arrayKey: 'leakPods',
                      })
                    )
                  }
                />
                <Column
                  key="evac"
                  title="Evac"
                  sorter={(a, b) =>
                    this.compareAssetCounts(a, b, {
                      assetType: 'evac',
                      statsKey: 'evacPod',
                      arrayKey: 'evacPods',
                    })
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'evac') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'evac',
                        statsKey: 'evacPod',
                        arrayKey: 'evacPods',
                      })
                    )
                  }
                />
                <Column
                  key="meter"
                  title="Meter"
                  sorter={(a, b) =>
                    this.compareAssetCounts(a, b, {
                      assetType: 'meter',
                      statsKey: 'flowMonitor',
                      arrayKey: 'flowMonitors',
                    })
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'meter') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'meter',
                        statsKey: 'flowMonitor',
                        arrayKey: 'flowMonitors',
                      })
                    )
                  }
                />
                <Column
                  key="valve"
                  title="Valve"
                  sorter={(a, b) =>
                    this.compareAssetCounts(a, b, {
                      assetType: 'valve',
                      statsKey: 'waterValve',
                      arrayKey: 'waterValves',
                      altArrayKey: 'valves',
                    })
                  }
                  sortOrder={
                    this.sortColumnKeyMatches(sortedInfo, 'valve') &&
                    sortedInfo.order
                  }
                  render={(text, record) =>
                    this.renderCount(
                      this.getAssetCount({
                        record,
                        assetType: 'valve',
                        statsKey: 'waterValve',
                        arrayKey: 'waterValves',
                        altArrayKey: 'valves',
                      })
                    )
                  }
                />
              </Table>
            </div>
          </div>
        )}
      </section>
    )
  }
}

export default BillingReport
